import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Building2, ShieldCheck, Sparkles, Globe, Mail, Phone, MapPin, Linkedin, Instagram,
  Loader2, Calendar, Users, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InquiryDialog = ({ company, open, onOpenChange }) => {
  const { user, isAuthenticated, api } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    requester_name: '', requester_company: '', requester_email: '', requester_phone: '', requirement: '',
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        requester_name: f.requester_name || user.name || '',
        requester_email: f.requester_email || user.email || '',
        requester_phone: f.requester_phone || user.phone || '',
        requester_company: f.requester_company || user.organization_name || '',
      }));
    }
  }, [user]);

  const submit = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/ecosystem/c/${company.slug}`)}`);
      return;
    }
    if (form.requirement.trim().length < 20) {
      toast.error('Please describe your requirement in at least 20 characters');
      return;
    }
    setSending(true);
    try {
      await api.post('/ecosystem/inquiries', { ...form, company_id: company.id });
      toast.success("Inquiry sent! Our centre manager will reach out shortly to introduce you.");
      onOpenChange(false);
      setForm((f) => ({ ...f, requirement: '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Inquire with {company.company_name}</DialogTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Your message goes to our Centre Manager at <strong>{company.primary_venue_name}</strong>, who will
            personally introduce you. No spam, no direct contact details shared.
          </p>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Your name *" value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} data-testid="inq-name" />
            <Input placeholder="Your company" value={form.requester_company} onChange={(e) => setForm({ ...form, requester_company: e.target.value })} data-testid="inq-company" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="email" placeholder="Email *" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} data-testid="inq-email" />
            <Input placeholder="Phone *" value={form.requester_phone} onChange={(e) => setForm({ ...form, requester_phone: e.target.value })} data-testid="inq-phone" />
          </div>
          <Textarea
            placeholder="Tell us what you're looking for — services, scope, timeline, budget range... (20+ chars)"
            rows={5}
            value={form.requirement}
            onChange={(e) => setForm({ ...form, requirement: e.target.value })}
            data-testid="inq-requirement"
          />
          <p className="text-xs text-muted-foreground">{form.requirement.length}/2000 chars</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={sending} className="bg-[#2d2a26] hover:bg-[#1d1a16] text-[#c8a97e]" data-testid="inq-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EcosystemProfilePage = () => {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInq, setOpenInq] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/ecosystem/companies/${slug}`)
      .then((r) => setCompany(r.data))
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#c8a97e]" /></div>;
  }
  if (!company) {
    return (
      <div className="min-h-screen bg-[#faf6ec] flex items-center justify-center px-6 text-center">
        <div>
          <Building2 className="w-12 h-12 mx-auto text-[#c8a97e] mb-3" />
          <h2 className="text-xl font-heading font-bold text-[#2d2a26] mb-2">Company not found</h2>
          <p className="text-sm text-[#7a7268] mb-5">This profile may be pending approval or no longer active.</p>
          <Link to="/ecosystem"><Button>Back to directory</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf6ec]" data-testid="ecosystem-profile">
      {/* Banner */}
      <div
        className="h-44 sm:h-60 bg-gradient-to-br from-[#e8dcc4] to-[#c8a97e] relative"
        style={company.banner_url ? { backgroundImage: `url(${company.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d2a26]/60 to-transparent" />
        <div className="max-w-5xl mx-auto px-6 pt-6 relative">
          <Link to="/ecosystem" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm bg-black/30 backdrop-blur px-3 py-1.5 rounded-full">
            <ArrowLeft className="w-4 h-4" /> Back to directory
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-16 relative pb-16">
        <Card className="border-[#e5dfd0] shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="w-24 h-24 rounded-2xl bg-white border-2 border-[#e5dfd0] flex items-center justify-center overflow-hidden shadow shrink-0 -mt-12 sm:-mt-16">
                {company.logo_url
                  ? <img src={company.logo_url} alt={company.company_name} className="w-full h-full object-cover" />
                  : <Building2 className="w-10 h-10 text-[#c8a97e]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="font-heading font-bold text-2xl sm:text-3xl text-[#2d2a26]">{company.company_name}</h1>
                  {company.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3 mr-1" /> Verified</Badge>}
                  {company.is_featured && <Badge className="bg-[#2d2a26] text-[#c8a97e]"><Sparkles className="w-3 h-3 mr-1" /> Featured</Badge>}
                </div>
                <p className="text-sm text-[#7a7268] mb-3">{company.industry}{company.company_size ? ` · ${company.company_size} people` : ''}{company.founded_year ? ` · Founded ${company.founded_year}` : ''}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(company.categories || []).map((c) => (
                    <Badge key={c.id} variant="outline" className="border-[#e5dfd0]">{c.name}</Badge>
                  ))}
                </div>
              </div>
              <div className="shrink-0">
                <Button
                  onClick={() => setOpenInq(true)}
                  className="bg-[#2d2a26] hover:bg-[#1d1a16] text-[#c8a97e] font-semibold"
                  data-testid="open-inquiry-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Send Inquiry
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="md:col-span-2 space-y-6">
                <section>
                  <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-2">ABOUT</h3>
                  <p className="text-sm text-[#2d2a26] leading-relaxed whitespace-pre-wrap">{company.about}</p>
                </section>
                {company.services_summary && (
                  <section>
                    <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-2">WHAT WE OFFER</h3>
                    <p className="text-sm text-[#2d2a26] leading-relaxed whitespace-pre-wrap">{company.services_summary}</p>
                  </section>
                )}
                {(company.expertise_tags || []).length > 0 && (
                  <section>
                    <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-2">EXPERTISE</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {company.expertise_tags.map((t) => (
                        <Badge key={t} className="bg-[#faf6ec] text-[#5c5750] border border-[#e5dfd0]">#{t}</Badge>
                      ))}
                    </div>
                  </section>
                )}
              </div>
              <aside className="space-y-3">
                <div className="bg-[#faf6ec] rounded-xl p-4 border border-[#e5dfd0] space-y-2.5">
                  <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-1">RESIDES AT</h3>
                  <div className="flex items-start gap-2 text-sm text-[#2d2a26]">
                    <MapPin className="w-4 h-4 text-[#c8a97e] mt-0.5 shrink-0" />
                    <span>Clayworks · {company.primary_venue_name}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-[#e5dfd0] space-y-2.5">
                  <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-1">REACH OUT</h3>
                  <p className="text-xs text-[#7a7268] mb-2">All inquiries routed through your Centre Manager.</p>
                  <Button onClick={() => setOpenInq(true)} variant="outline" className="w-full" data-testid="aside-inquiry-btn">
                    <Send className="w-4 h-4 mr-2" /> Send Inquiry
                  </Button>
                </div>
                {(company.website_url || company.linkedin_url || company.instagram_url) && (
                  <div className="bg-white rounded-xl p-4 border border-[#e5dfd0] space-y-2.5">
                    <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268] mb-1">ELSEWHERE</h3>
                    {company.website_url && (
                      <a href={company.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#2d2a26] hover:text-[#c8a97e]">
                        <Globe className="w-4 h-4" /> Website
                      </a>
                    )}
                    {company.linkedin_url && (
                      <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#2d2a26] hover:text-[#c8a97e]">
                        <Linkedin className="w-4 h-4" /> LinkedIn
                      </a>
                    )}
                    {company.instagram_url && (
                      <a href={company.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#2d2a26] hover:text-[#c8a97e]">
                        <Instagram className="w-4 h-4" /> Instagram
                      </a>
                    )}
                  </div>
                )}
              </aside>
            </div>
          </CardContent>
        </Card>
      </div>
      <InquiryDialog company={company} open={openInq} onOpenChange={setOpenInq} />
    </div>
  );
};

export default EcosystemProfilePage;
